// Audio for the guided workout player. Plays the pre-rendered "Madison" mp3 for a
// cue when one exists (built into assets/audio + manifest.json by `npm run
// gen:voice`), and falls back to the browser's Speech Synthesis when it doesn't.
// Also emits short WebAudio beeps so transitions/countdowns cut through wind on a
// field. Browsers block audio until a user gesture, so call unlock() from the
// Start / Test-sound tap before relying on say()/beep().
// ponytail: module singleton — there's one speaker (the leader's device), no need
// to thread an instance through the tree.
import { PLAYBACK_RATE } from "./workouts.js";

let manifestPromise = null;
const loadManifest = () => {
  if (!manifestPromise) {
    manifestPromise = fetch("assets/audio/manifest.json")
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return manifestPromise;
};

const hasTTS = typeof window !== "undefined" && "speechSynthesis" in window;
let audioCtx = null;
let current = null; // the HTMLAudioElement playing right now, if any
let muted = false;

export const isMuted = () => muted;
export const setMuted = (value) => {
  muted = value;
  if (muted) stop();
};

// Prime the audio engine from a user gesture. Resumes/creates the WebAudio context
// (for beeps) and preloads the manifest. Safe to call more than once.
export const unlock = async () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      audioCtx = audioCtx || new Ctx();
      if (audioCtx.state === "suspended") await audioCtx.resume();
    }
  } catch { /* no WebAudio — beeps just no-op */ }
  await loadManifest();
};

export const stop = () => {
  if (current) { try { current.pause(); } catch { /* ignore */ } current = null; }
  if (hasTTS) window.speechSynthesis.cancel();
};

const speakTTS = (phrase) => new Promise((resolve) => {
  if (!hasTTS || muted) { resolve(); return; }
  const utterance = new SpeechSynthesisUtterance(phrase);
  utterance.rate = PLAYBACK_RATE;
  utterance.onend = resolve;
  utterance.onerror = resolve;
  window.speechSynthesis.speak(utterance);
});

// Speak a phrase, cancelling anything in flight so advancing never overlaps.
// Resolves when the cue finishes (or immediately if muted/unavailable).
export const say = async (phrase) => {
  if (muted || !phrase) return;
  stop();
  const manifest = await loadManifest();
  const rel = manifest[phrase];
  if (!rel) { await speakTTS(phrase); return; }
  await new Promise((resolve) => {
    const audio = new Audio("assets/" + rel);
    audio.playbackRate = PLAYBACK_RATE;
    current = audio;
    const done = () => { if (current === audio) current = null; resolve(); };
    audio.onended = done;
    audio.onerror = () => { done(); speakTTS(phrase); };
    audio.play().catch(() => { done(); speakTTS(phrase); });
  });
};

// Short tone for a transition (higher = "go") or a countdown tick. No-op until
// unlock() has run or if muted.
export const beep = (freq = 880, ms = 130) => {
  if (muted || !audioCtx) return;
  try {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    const t = audioCtx.currentTime;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.35, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + ms / 1000);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(t);
    osc.stop(t + ms / 1000);
  } catch { /* ignore */ }
};
